/**
 * AnalyticsScreen — Percento analysis screen (`/analytics`).
 *
 * Pixel-faithful port of `design/project/screens-analytics.jsx` (the
 * `AnalyticsScreen` function).
 *
 * Layout (top → bottom):
 *   1. Title row + 支出/收入 mode toggle.
 *   2. 月/年/自訂 period kind segmented control.
 *   3. Period nav: stepper for month/year, custom date-range button → DateRangeSheet.
 *   4. Summary card with total amount + MoM/YoY CompareChip + TrendChart.
 *   5. Per-row category breakdown (PercentoBar).
 *   6. "其他" collapsible roll-up of <5% slices (separate from top-N).
 *
 * Data flow:
 *   - Reads transactions / categories / active book / primary currency from
 *     the zustand store.
 *   - Aggregation memoised through `analyticsCache` slice.
 *   - Compare period (上月 / 去年) is also aggregated via buildPercento;
 *     only the total is used for the chip.
 *
 * Note on custom range: the design uses a free-form M/D string. We accept
 * an ISO YYYY-MM-DD range; default values default to today and today minus
 * 30 days. The DateRangeSheet uses native date inputs internally.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
} from 'date-fns';

import Icon from '@/components/common/Icon';
import { buildPercento, type PercentoResult } from '@/lib/analytics/percento';
import { formatAmount } from '@/lib/format/currency';
import { formatYmd } from '@/lib/format/date';
import { dayPart } from '@/lib/format/datetime';
import { convertMinor } from '@/lib/format/fx';
import { useStore } from '@/state/store';
import type { BookRow, CategoryRow, TransactionRow, TxKind } from '@/lib/sheets/types';
import PercentoBar from './PercentoBar';
import EmptyAnalytics from './EmptyAnalytics';
import AnalyticsDrilldown from './AnalyticsDrilldown';

export type PeriodKind = 'month' | 'year' | 'custom' | 'all';
/** v5: 'overview' renders BOTH expense and income breakdowns side by side
 *  so users can see the income/expense balance for a period without
 *  flipping back and forth between the two single-mode tabs. */
export type AnalyticsMode = 'overview' | 'expense' | 'income';

const MINOR_PCT_THRESHOLD = 5; // <5% rolled into 「其他」

export interface AnalyticsScreenProps {
  /** Override "today" — handy for tests / Storybook. Defaults to `new Date()`. */
  today?: Date;
  /** Default mode. */
  initialMode?: AnalyticsMode;
  /** Default period kind. */
  initialPeriodKind?: PeriodKind;
  /** Currency to compute against. Defaults to `TWD`. */
  currency?: string;
  /** Callback when the user taps a category row → opens drill-down. */
  onCategoryTap?: (categoryId: string) => void;
}

function shiftMonth(date: Date, dir: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + dir, 1);
}

function shiftYear(date: Date, dir: number): Date {
  return new Date(date.getFullYear() + dir, 0, 1);
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function isSameYear(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear();
}

function dateBoundsForKind(
  kind: PeriodKind,
  cursor: Date,
  custom: { start: string; end: string },
  activeBook: BookRow | undefined,
  today: Date,
): { start: string; end: string } {
  if (kind === 'month') {
    return {
      start: formatYmd(startOfMonth(cursor)),
      end: formatYmd(endOfMonth(cursor)),
    };
  }
  if (kind === 'year') {
    return {
      start: formatYmd(startOfYear(cursor)),
      end: formatYmd(endOfYear(cursor)),
    };
  }
  if (kind === 'all') {
    return {
      start: activeBook ? formatYmd(new Date(activeBook.created_at)) : '',
      end: todayIso(today),
    };
  }
  return custom;
}

function priorPeriod(
  kind: PeriodKind,
  cursor: Date,
  _custom: { start: string; end: string },
): { start: string; end: string; label: string } | null {
  if (kind === 'month') {
    const prev = shiftMonth(cursor, -1);
    return {
      start: formatYmd(startOfMonth(prev)),
      end: formatYmd(endOfMonth(prev)),
      label: 'vs 上月',
    };
  }
  if (kind === 'year') {
    const prev = shiftYear(cursor, -1);
    return {
      start: formatYmd(startOfYear(prev)),
      end: formatYmd(endOfYear(prev)),
      label: 'vs 去年',
    };
  }
  return null;
}

function todayIso(d: Date): string {
  return formatYmd(d);
}

export default function AnalyticsScreen({
  today: todayProp,
  initialMode = 'expense',
  initialPeriodKind = 'month',
  currency = 'TWD',
  onCategoryTap,
}: AnalyticsScreenProps): JSX.Element {
  const today = todayProp ?? new Date();

  const transactions = useStore((s) => s.transactions.items);
  const categories = useStore((s) => s.categories.items);
  const activeBookId = useStore((s) => s.books.activeBookId);
  const books = useStore((s) => s.books.items);

  const cacheGet = useStore((s) => s.analyticsCache.get);
  const cacheSet = useStore((s) => s.analyticsCache.set);
  const cacheClear = useStore((s) => s.analyticsCache.clear);

  const [mode, setMode] = useState<AnalyticsMode>(initialMode);
  const [periodKind, setPeriodKind] = useState<PeriodKind>(initialPeriodKind);
  const [cursor, setCursor] = useState<Date>(today);
  const [customRange, setCustomRange] = useState<{ start: string; end: string }>(() => {
    const end = today;
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { start: formatYmd(start), end: formatYmd(end) };
  });
  const [rangeSheetOpen, setRangeSheetOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  // Internal drill-down state — opens an overlay when no external
  // `onCategoryTap` is provided. Keeps the screen self-contained.
  const [drillCategoryId, setDrillCategoryId] = useState<string | null>(null);

  const activeBook = useMemo(
    () => books.find((b) => b.id === activeBookId),
    [books, activeBookId],
  );

  const period = useMemo(
    () => dateBoundsForKind(periodKind, cursor, customRange, activeBook, today),
    [periodKind, cursor, customRange, activeBook, today],
  );

  const compare = useMemo(
    () => priorPeriod(periodKind, cursor, customRange),
    [periodKind, cursor, customRange],
  );

  // Filter transactions to active book.
  const bookScopedTx = useMemo(() => {
    if (!activeBookId) return transactions;
    return transactions.filter((t) => t.book_id === activeBookId);
  }, [transactions, activeBookId]);

  // Fingerprint the visible tx set so adding / editing / deleting any row
  // bumps the cache key. Without this, the slice cache returns a stale
  // PercentoResult after a write because the period/mode/currency tuple
  // alone doesn't change. Length covers add/delete; the max `updated_at`
  // covers edits (every write bumps `updated_at` server-side and locally).
  const txFingerprint = useMemo(() => {
    let maxUpdated = '';
    for (const t of bookScopedTx) {
      if (t.updated_at > maxUpdated) maxUpdated = t.updated_at;
    }
    return `${bookScopedTx.length}:${maxUpdated}`;
  }, [bookScopedTx]);

  // For overview mode the chart slot below shows BOTH expense and income
  // breakdowns; we always compute each, then pick the right one for the
  // primary `result` (used by trend / compare). 'overview' falls back to
  // expense for the main chart slot — the second income chart is rendered
  // separately below.
  const expenseResult = useMemo<PercentoResult>(
    () =>
      buildPercento(bookScopedTx, categories, {
        mode: 'expense',
        period,
        currency,
        // AnalyticsScreen does its own <5% bucketing into an expandable
        // 「其他」 rollup; disable the library's TOP_N=6 lump so every
        // slice flows through.
        topN: Infinity,
      }),
    [bookScopedTx, categories, period, currency],
  );
  const incomeResult = useMemo<PercentoResult>(
    () =>
      buildPercento(bookScopedTx, categories, {
        mode: 'income',
        period,
        currency,
        topN: Infinity,
      }),
    [bookScopedTx, categories, period, currency],
  );

  const cacheKey = `${activeBookId ?? '*'}:${period.start}:${period.end}:${mode}:${currency}:${txFingerprint}`;

  const result: PercentoResult = useMemo(() => {
    if (mode === 'overview') {
      // Header summary becomes the NET (income − expense). Slices stay
      // on the dedicated overview render below; we just need a result-
      // shaped object whose total_minor drives the hero figure.
      return {
        slices: [],
        otherSlice: null,
        total_minor: incomeResult.total_minor - expenseResult.total_minor,
        currency,
      };
    }
    const cached = cacheGet(cacheKey);
    if (cached) return cached;
    return buildPercento(bookScopedTx, categories, {
      mode,
      period,
      currency,
      topN: Infinity,
    });
  }, [
    mode,
    cacheGet,
    cacheKey,
    bookScopedTx,
    categories,
    period,
    currency,
    expenseResult.total_minor,
    incomeResult.total_minor,
  ]);

  // Compute compare-period total (no cache — single number).
  const compareTotal = useMemo<number | null>(() => {
    if (!compare) return null;
    if (mode === 'overview') {
      const inc = buildPercento(bookScopedTx, categories, {
        mode: 'income',
        period: { start: compare.start, end: compare.end },
        currency,
      });
      const exp = buildPercento(bookScopedTx, categories, {
        mode: 'expense',
        period: { start: compare.start, end: compare.end },
        currency,
      });
      return inc.total_minor - exp.total_minor;
    }
    const r = buildPercento(bookScopedTx, categories, {
      mode,
      period: { start: compare.start, end: compare.end },
      currency,
    });
    return r.total_minor;
  }, [compare, bookScopedTx, categories, mode, currency]);

  // Drop stale entries whenever the underlying tx data fingerprint shifts
  // — keeps the cache from accumulating one entry per (period × write).
  useEffect(() => {
    cacheClear();
  }, [txFingerprint, cacheClear]);

  // Side-effect: write to cache after compute (skip if already there).
  useEffect(() => {
    if (cacheGet(cacheKey)) return;
    cacheSet(cacheKey, result);
  }, [cacheKey, cacheGet, cacheSet, result]);

  const periodLabel = useMemo(() => {
    if (periodKind === 'month') {
      return `${cursor.getFullYear()} · ${cursor.getMonth() + 1} 月`;
    }
    if (periodKind === 'year') return `${cursor.getFullYear()} 年`;
    if (periodKind === 'all') return `${period.start || '最早'} – ${period.end}`;
    return `${customRange.start} – ${customRange.end}`;
  }, [cursor, periodKind, customRange, period]);

  const nextDisabled = useMemo(() => {
    if (periodKind === 'month') return isSameMonth(cursor, today);
    if (periodKind === 'year') return isSameYear(cursor, today);
    return true;
  }, [cursor, periodKind, today]);

  const accent = mode === 'expense' ? 'var(--expense)' : 'var(--income)';
  const isEmpty = result.total_minor === 0;

  // Re-bucket slices into major (≥5%) and minor (<5%) so we render the
  // "其他" collapsible roll-up like the design.
  const { major, minor, minorSum, minorPct } = useMemo(() => {
    const all = result.otherSlice
      ? [...result.slices, result.otherSlice]
      : result.slices;
    const M = all.filter((s) => s.pct >= MINOR_PCT_THRESHOLD);
    const m = all.filter((s) => s.pct < MINOR_PCT_THRESHOLD);
    const sum = m.reduce((a, b) => a + b.total_minor, 0);
    const pct = result.total_minor > 0 ? (sum / result.total_minor) * 100 : 0;
    return { major: M, minor: m, minorSum: sum, minorPct: pct };
  }, [result]);

  // PercentoBar gets only the major slices and no "其他" — we render the
  // collapsible separately.
  const majorOnlyResult = useMemo<PercentoResult>(
    () => ({
      slices: major,
      otherSlice: null,
      total_minor: result.total_minor,
      currency: result.currency,
    }),
    [major, result.total_minor, result.currency],
  );

  // Trend data — overview falls back to expense for the trend chart;
  // the dual-chart breakdown below covers income separately.
  const trend = useMemo(() => {
    const trendMode: TxKind = mode === 'overview' ? 'expense' : mode;
    if (periodKind === 'month') {
      return monthlyTrend(bookScopedTx, trendMode, currency, cursor, today);
    }
    if (periodKind === 'year') {
      return yearlyTrend(bookScopedTx, trendMode, currency, cursor, today);
    }
    return null;
  }, [periodKind, bookScopedTx, mode, currency, cursor, today]);

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-app)',
      }}
    >
      {/* Header */}
      <div style={{ padding: '60px 20px 10px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1
            style={{
              font: '600 26px var(--font-cjk-display)',
              color: 'var(--fg-1)',
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            分析
          </h1>
          <SegmentedToggle<AnalyticsMode>
            value={mode}
            onChange={(v) => {
              setMode(v);
            }}
            options={[
              { value: 'overview', label: '全部' },
              { value: 'expense', label: '支出' },
              { value: 'income', label: '收入' },
            ]}
            ariaLabel="支出 / 收入 / 全部切換"
          />
        </div>

        {/* Period kind */}
        <div style={{ marginTop: 14 }}>
          <SegmentedToggle<PeriodKind>
            value={periodKind}
            onChange={(v) => {
              setPeriodKind(v);
              if (v === 'custom') setRangeSheetOpen(true);
            }}
            options={[
              { value: 'month', label: '月' },
              { value: 'year', label: '年' },
              { value: 'custom', label: '自訂' },
              { value: 'all', label: '全部' },
            ]}
            ariaLabel="期間切換"
            small
          />
        </div>

        {/* Period nav */}
        <div style={{ marginTop: 10 }}>
          {periodKind === 'month' || periodKind === 'year' ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setCursor((c) =>
                    periodKind === 'month' ? shiftMonth(c, -1) : shiftYear(c, -1),
                  )
                }
                aria-label="上一期"
                style={navBtn}
              >
                <Icon name="chevL" size={14} stroke="var(--fg-2)" />
              </button>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  font: '500 14px var(--font-cjk)',
                  color: 'var(--fg-1)',
                }}
              >
                {periodLabel}
              </div>
              <button
                type="button"
                onClick={() =>
                  setCursor((c) =>
                    periodKind === 'month' ? shiftMonth(c, 1) : shiftYear(c, 1),
                  )
                }
                aria-label="下一期"
                disabled={nextDisabled}
                style={{
                  ...navBtn,
                  cursor: nextDisabled ? 'default' : 'pointer',
                  opacity: nextDisabled ? 0.3 : 1,
                }}
              >
                <Icon name="chevR" size={14} stroke="var(--fg-2)" />
              </button>
            </div>
          ) : periodKind === 'custom' ? (
            <button
              type="button"
              onClick={() => setRangeSheetOpen(true)}
              aria-label="設定自訂日期區間"
              style={{
                width: '100%',
                padding: '8px 14px',
                background: 'var(--bg-raised)',
                border: 0,
                borderRadius: 12,
                font: '500 13px var(--font-cjk)',
                color: 'var(--fg-1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Icon name="calendar" size={14} stroke="var(--fg-3)" />
              {customRange.start} – {customRange.end}
            </button>
          ) : (
            <div
              aria-label="全部期間範圍"
              style={{
                width: '100%',
                padding: '8px 14px',
                background: 'var(--bg-raised)',
                borderRadius: 12,
                font: '500 13px var(--font-cjk)',
                color: 'var(--fg-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Icon name="calendar" size={14} stroke="var(--fg-3)" />
              {periodLabel}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 20px 120px' }}>
        {/* Summary */}
        <div
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 16,
            padding: '14px 16px',
            boxShadow: 'var(--shadow-1)',
            marginTop: 8,
          }}
        >
          <div
            style={{
              font: '500 11px var(--font-cjk)',
              color: 'var(--fg-3)',
              letterSpacing: '0.06em',
            }}
          >
            {mode === 'expense' ? '支出總計' : '收入總計'} · {periodLabel}
          </div>
          <div
            style={{
              font: '600 30px var(--font-num)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
              color: 'var(--fg-1)',
              marginTop: 2,
            }}
          >
            {formatAmount(result.total_minor, currency)}
          </div>

          {/* Compare chip — MoM / YoY */}
          {compare && compareTotal !== null && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <CompareChip
                label={compare.label}
                pct={
                  compareTotal > 0
                    ? ((result.total_minor - compareTotal) / compareTotal) * 100
                    : 0
                }
                mode={mode === 'overview' ? 'expense' : mode}
              />
            </div>
          )}

          {/* Trend chart */}
          {trend && trend.values.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <TrendChart
                values={trend.values}
                compare={trend.compare}
                labels={trend.labels}
                fullLength={trend.fullLength}
                color={accent}
                periodLabel={trend.periodLabel}
                comparePeriodLabel={trend.compareLabel}
                unit={currency}
              />
            </div>
          )}
        </div>

        {mode === 'overview' ? (
          // Overview shows BOTH breakdowns side by side so the user can
          // see the income / expense balance at a glance.
          <OverviewBreakdowns
            expense={expenseResult}
            income={incomeResult}
            categories={categories}
            onCategoryTap={(id) => {
              if (onCategoryTap) onCategoryTap(id);
              else setDrillCategoryId(id);
            }}
          />
        ) : isEmpty ? (
          <div style={{ marginTop: 18 }}>
            <EmptyAnalytics
              caption={
                mode === 'expense' ? '本期間還沒有支出記錄' : '本期間還沒有收入記錄'
              }
              hint="記一筆，這裡就會亮起來"
            />
          </div>
        ) : (
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 2px 4px',
              }}
            >
              <div
                style={{
                  font: '600 13px var(--font-cjk)',
                  color: 'var(--fg-1)',
                }}
              >
                分類佔比
              </div>
              <div
                style={{
                  font: '400 11.5px var(--font-cjk)',
                  color: 'var(--fg-3)',
                }}
              >
                {major.length + (minor.length > 0 ? 1 : 0)} 類
              </div>
            </div>
            <div
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 14,
                padding: '4px 14px',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              <PercentoBar
                result={majorOnlyResult}
                onSelectSlice={(id) => {
                  if (!id) return;
                  if (onCategoryTap) onCategoryTap(id);
                  else setDrillCategoryId(id);
                }}
                categories={categories}
                variant="plain"
              />
              {minor.length > 0 && (
                <OtherRollup
                  open={otherOpen}
                  onToggle={() => setOtherOpen((v) => !v)}
                  count={minor.length}
                  sum={minorSum}
                  pct={minorPct}
                  currency={currency}
                  items={minor.map((s) => ({
                    id: s.category_id,
                    name: s.name,
                    color: `var(${s.color_token})`,
                    total_minor: s.total_minor,
                    pct: s.pct,
                  }))}
                  onItemTap={(id) => {
                    if (onCategoryTap) onCategoryTap(id);
                    else setDrillCategoryId(id);
                  }}
                  hasMajor={major.length > 0}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {rangeSheetOpen && (
        <DateRangeSheet
          current={customRange}
          onClose={() => setRangeSheetOpen(false)}
          onPick={(r) => {
            setCustomRange(r);
            setRangeSheetOpen(false);
          }}
          today={today}
        />
      )}

      {drillCategoryId && (() => {
        // Derive drilldown mode from the tapped category's own kind. In
        // overview mode the previous code hard-coded 'expense', so tapping
        // an income slice (or one of the small categories rolled into
        // 「其他」 on the income side) opened a drilldown filtered for
        // expenses → "no records" because that cat never has expenses.
        // In single-mode views (expense/income) the cat.kind always
        // matches anyway, so this is also correct there.
        const cat = categories.find((c) => c.id === drillCategoryId);
        const drillMode: 'expense' | 'income' =
          cat?.kind === 'income' ? 'income' : 'expense';
        return (
          <AnalyticsDrilldown
            categoryId={drillCategoryId}
            period={period}
            mode={drillMode}
            currency={currency}
            periodLabel={periodLabel}
            onClose={() => setDrillCategoryId(null)}
          />
        );
      })()}
    </div>
  );
}

/**
 * Renders 收入分類佔比 + 支出分類佔比 stacked, used by the overview tab.
 * Each block is the same composition the per-mode view uses (header +
 * PercentoBar inside a raised card) so users get visual continuity when
 * switching tabs.
 */
function OverviewBreakdowns({
  expense,
  income,
  categories,
  onCategoryTap,
}: {
  expense: PercentoResult;
  income: PercentoResult;
  categories: CategoryRow[];
  onCategoryTap: (id: string) => void;
}): JSX.Element {
  const sections: Array<{ title: string; result: PercentoResult; emptyText: string }> = [
    { title: '收入分類佔比', result: income, emptyText: '本期間還沒有收入記錄' },
    { title: '支出分類佔比', result: expense, emptyText: '本期間還沒有支出記錄' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 18 }}>
      {sections.map((s) => (
        <div key={s.title}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 2px 4px',
            }}
          >
            <div style={{ font: '600 13px var(--font-cjk)', color: 'var(--fg-1)' }}>
              {s.title}
            </div>
            <div style={{ font: '400 11.5px var(--font-cjk)', color: 'var(--fg-3)' }}>
              {s.result.slices.length + (s.result.otherSlice ? 1 : 0)} 類
            </div>
          </div>
          {s.result.total_minor === 0 ? (
            <EmptyAnalytics caption={s.emptyText} hint="記一筆，這裡就會亮起來" />
          ) : (
            <div
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 14,
                padding: '4px 14px',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              <PercentoBar
                result={s.result}
                onSelectSlice={(id) => {
                  if (!id) return;
                  onCategoryTap(id);
                }}
                categories={categories}
                variant="plain"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const navBtn = {
  width: 30,
  height: 30,
  borderRadius: 15,
  background: 'var(--bg-raised)',
  border: 0,
  cursor: 'pointer' as const,
  display: 'grid' as const,
  placeItems: 'center' as const,
};

interface SegOpt<T> {
  value: T;
  label: string;
}

function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  small,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegOpt<T>[];
  ariaLabel: string;
  small?: boolean;
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        background: 'var(--bg-raised)',
        padding: 3,
        borderRadius: 999,
        font: small
          ? '500 12.5px var(--font-cjk)'
          : '500 13px var(--font-cjk)',
      }}
    >
      {options.map((o) => {
        const isActive = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(o.value)}
            style={{
              border: 0,
              background: isActive ? 'var(--bg-surface)' : 'transparent',
              padding: small ? '5px 13px' : '5px 14px',
              borderRadius: 999,
              color: isActive ? 'var(--fg-1)' : 'var(--fg-2)',
              boxShadow: isActive ? 'var(--shadow-1)' : 'none',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── CompareChip ─────────────────────────────────────────────────────────
export interface CompareChipProps {
  label: string;
  pct: number;
  mode: 'expense' | 'income';
}

export function CompareChip({ label, pct, mode }: CompareChipProps): JSX.Element {
  const up = pct > 0;
  // For 支出: down is good. For 收入: up is good.
  const isGood = mode === 'expense' ? !up : up;
  const isFlat = Math.abs(pct) < 0.5;
  const color = isFlat
    ? 'var(--fg-3)'
    : isGood
      ? 'var(--income)'
      : 'var(--expense)';
  const arrow = isFlat ? '·' : up ? '↑' : '↓';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        background: 'var(--bg-raised)',
        borderRadius: 999,
        font: '500 11.5px var(--font-cjk)',
        color: 'var(--fg-2)',
      }}
    >
      <span>{label}</span>
      <span
        style={{
          color,
          font: '600 12px var(--font-num)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {arrow} {Math.abs(pct).toFixed(1)}%
      </span>
    </div>
  );
}

// ─── OtherRollup ─────────────────────────────────────────────────────────
interface OtherRollupItem {
  id: string;
  name: string;
  color: string;
  total_minor: number;
  pct: number;
}

interface OtherRollupProps {
  open: boolean;
  onToggle: () => void;
  count: number;
  sum: number;
  pct: number;
  currency: string;
  items: OtherRollupItem[];
  onItemTap: (id: string) => void;
  hasMajor: boolean;
}

function OtherRollup({
  open,
  onToggle,
  count,
  sum,
  pct,
  currency,
  items,
  onItemTap,
  hasMajor,
}: OtherRollupProps): JSX.Element {
  const widthPct = Math.max(2, pct);
  return (
    <div
      style={{
        borderTop: hasMajor ? '0.5px solid var(--border-1)' : '0',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          border: 0,
          background: 'transparent',
          padding: '10px 0',
          cursor: 'pointer',
          display: 'block',
          font: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 6,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'var(--sand-400)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="dots" size={14} stroke="#fff" />
          </span>
          <span
            style={{
              flex: 1,
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            其他{' '}
            <span
              style={{
                font: '400 11.5px var(--font-cjk)',
                color: 'var(--fg-3)',
                marginLeft: 4,
              }}
            >
              {count} 類 &lt; {MINOR_PCT_THRESHOLD}%
            </span>
          </span>
          <span
            style={{
              font: '500 13px var(--font-num)',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--fg-2)',
            }}
          >
            {formatAmount(sum, currency)}
          </span>
          <span
            style={{
              minWidth: 42,
              textAlign: 'right',
              font: '600 13px var(--font-num)',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--fg-1)',
            }}
          >
            {pct.toFixed(0)}%
          </span>
          <span
            style={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 160ms',
              display: 'inline-flex',
            }}
          >
            <Icon name="chevD" size={14} stroke="var(--fg-3)" />
          </span>
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: 'var(--cream-200)',
            overflow: 'hidden',
            marginLeft: 38,
          }}
        >
          <div
            style={{
              width: `${widthPct}%`,
              height: '100%',
              background: 'var(--sand-400)',
              borderRadius: 3,
            }}
          />
        </div>
      </button>

      {open && (
        <div
          style={{
            marginLeft: 38,
            paddingBottom: 8,
            borderTop: '0.5px solid var(--border-1)',
          }}
        >
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              onClick={() => onItemTap(it.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '8px 0',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                borderBottom:
                  i < items.length - 1 ? '0.5px solid var(--border-1)' : 0,
                font: 'inherit',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: it.color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  flex: 1,
                  font: '400 13px var(--font-cjk)',
                  color: 'var(--fg-2)',
                }}
              >
                {it.name}
              </span>
              <span
                style={{
                  font: '500 12px var(--font-num)',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--fg-2)',
                }}
              >
                {formatAmount(it.total_minor, currency)}
              </span>
              <span
                style={{
                  minWidth: 34,
                  textAlign: 'right',
                  font: '500 12px var(--font-num)',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--fg-3)',
                }}
              >
                {it.pct.toFixed(1)}%
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TrendChart ──────────────────────────────────────────────────────────
interface TrendChartProps {
  values: number[];
  compare?: number[];
  labels: string[];
  fullLength: number;
  color: string;
  compareColor?: string;
  periodLabel: string;
  comparePeriodLabel: string;
  unit: string;
}

export function TrendChart({
  values,
  compare,
  labels,
  fullLength,
  color,
  compareColor = 'var(--fg-4)',
  periodLabel,
  comparePeriodLabel,
  unit,
}: TrendChartProps): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const total = fullLength;
  const W = 300,
    H = 130,
    padL = 34,
    padR = 6,
    padT = 6,
    padB = 20;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const slotW = innerW / total;
  const barW = Math.max(3, Math.min(14, slotW * 0.55));

  const rawMax = Math.max(1, ...values, ...(compare ?? []));
  const niceMax = (() => {
    const exp = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const m = rawMax / exp;
    const nice = m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10;
    return nice * exp;
  })();
  const xAt = (i: number): number => padL + slotW * i + slotW / 2;
  const yAt = (v: number): number => padT + innerH - (v / niceMax) * innerH;

  const step = total > 20 ? Math.ceil(total / 7) : total > 10 ? 2 : 1;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => niceMax * t);
  const fmtY = (v: number): string => {
    // Format with currency-major where amounts are minor units. We treat
    // values as minor and show in "k" / "萬" using major (÷100) for legibility.
    const major = v / 100;
    if (major >= 10000) return (major / 10000).toFixed(major % 10000 === 0 ? 0 : 1) + '萬';
    if (major >= 1000) return (major / 1000).toFixed(major % 1000 === 0 ? 0 : 1) + 'k';
    return String(Math.round(major));
  };

  const cmpPts =
    compare?.map((v, i) => [xAt(i), yAt(v)] as [number, number]) ?? [];
  const cmpPath = cmpPts
    .map((p, i) => (i === 0 ? 'M' : 'L') + p.join(','))
    .join(' ');

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            font: '500 10px var(--font-cjk)',
            color: 'var(--fg-4)',
            letterSpacing: '0.06em',
          }}
        >
          單位：{unit}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            font: '500 11px var(--font-cjk)',
            color: 'var(--fg-3)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                background: color,
                borderRadius: 2,
                display: 'inline-block',
              }}
            />
            {periodLabel}
          </span>
          {compare && compare.length > 0 && (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <svg width="16" height="8" aria-hidden="true">
                <line
                  x1="0"
                  y1="4"
                  x2="16"
                  y2="4"
                  stroke={compareColor}
                  strokeWidth="1.5"
                  strokeDasharray="3 2"
                />
              </svg>
              {comparePeriodLabel}
            </span>
          )}
        </div>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: 'block', overflow: 'visible' }}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${periodLabel}趨勢圖`}
      >
        {yTicks.map((v, i) => {
          const y = yAt(v);
          return (
            <g key={`yt-${i}`}>
              <line
                x1={padL}
                y1={y}
                x2={W - padR}
                y2={y}
                stroke="var(--border-1)"
                strokeWidth="0.5"
                strokeDasharray={i === 0 ? '' : '2 3'}
                opacity={i === 0 ? 1 : 0.6}
              />
              <text
                x={padL - 5}
                y={y + 3}
                textAnchor="end"
                style={{ font: '400 9px var(--font-num)', fill: 'var(--fg-4)' }}
              >
                {fmtY(v)}
              </text>
            </g>
          );
        })}

        {values.map((v, i) => {
          const h = (v / niceMax) * innerH;
          const x = xAt(i) - barW / 2;
          const y = padT + innerH - h;
          const isHover = i === hover;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barW}
              height={h}
              rx="2"
              fill={color}
              opacity={isHover ? 1 : 0.78}
            />
          );
        })}

        {compare && compare.length > 1 && (
          <path
            d={cmpPath}
            stroke={compareColor}
            strokeWidth="1.2"
            fill="none"
            strokeDasharray="3 2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {hover != null && hover < values.length && (
          <g>
            <line
              x1={xAt(hover)}
              y1={padT}
              x2={xAt(hover)}
              y2={padT + innerH}
              stroke="var(--fg-4)"
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
            <text
              x={xAt(hover)}
              y={yAt(values[hover]!) - 4}
              textAnchor="middle"
              style={{ font: '600 9px var(--font-num)', fill: 'var(--fg-1)' }}
            >
              {(values[hover]! / 100).toLocaleString()}
            </text>
          </g>
        )}

        {Array.from({ length: total }, (_, i) => (
          <rect
            key={`hit-${i}`}
            x={padL + slotW * i}
            y={padT}
            width={slotW}
            height={innerH}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onTouchStart={() => setHover(i)}
          />
        ))}

        {labels.map((lab, i) => {
          if (i % step !== 0 && i !== total - 1) return null;
          return (
            <text
              key={`lab-${i}`}
              x={xAt(i)}
              y={H - 6}
              textAnchor="middle"
              style={{ font: '400 9px var(--font-num)', fill: 'var(--fg-4)' }}
            >
              {lab}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// Build a daily series for the cursor month and compare against prev month.
function monthlyTrend(
  txs: TransactionRow[],
  mode: TxKind,
  currency: string,
  cursor: Date,
  today: Date,
): {
  values: number[];
  compare: number[];
  labels: string[];
  fullLength: number;
  periodLabel: string;
  compareLabel: string;
} {
  const y = cursor.getFullYear();
  const m = cursor.getMonth(); // 0-indexed
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const isCurrentMonth =
    today.getFullYear() === y && today.getMonth() === m;
  const todayDay = isCurrentMonth ? today.getDate() : daysInMonth;

  const curValues = computeDailyTotals(txs, mode, currency, y, m, todayDay);
  const prevDate = new Date(y, m - 1, 1);
  const prevDays = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 0).getDate();
  const cmpValues = computeDailyTotals(
    txs,
    mode,
    currency,
    prevDate.getFullYear(),
    prevDate.getMonth(),
    Math.min(curValues.length, prevDays),
  );
  const labels = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
  return {
    values: curValues,
    compare: cmpValues,
    labels,
    fullLength: daysInMonth,
    periodLabel: '本月',
    compareLabel: '上月',
  };
}

function computeDailyTotals(
  txs: TransactionRow[],
  mode: TxKind,
  currency: string,
  year: number,
  monthIdx: number,
  upToDay: number,
): number[] {
  const out = Array(upToDay).fill(0) as number[];
  for (const t of txs) {
    if (t.deleted_at) continue;
    if (t.kind !== mode) continue;
    // v5: `t.date` may be `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`. Naively
    // appending `T00:00:00` produced `YYYY-MM-DDTHH:MMT00:00:00` for
    // imported rows → Invalid Date → tx silently dropped from the
    // trend chart. Strip to the day part before parsing.
    const d = new Date(dayPart(t.date) + 'T00:00:00');
    if (d.getFullYear() !== year || d.getMonth() !== monthIdx) continue;
    const day = d.getDate();
    if (day > upToDay) continue;
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', currency);
    out[day - 1] = (out[day - 1] ?? 0) + minor;
  }
  return out;
}

function yearlyTrend(
  txs: TransactionRow[],
  mode: TxKind,
  currency: string,
  cursor: Date,
  today: Date,
): {
  values: number[];
  compare: number[];
  labels: string[];
  fullLength: number;
  periodLabel: string;
  compareLabel: string;
} {
  const y = cursor.getFullYear();
  const isCurrent = today.getFullYear() === y;
  const endM = isCurrent ? today.getMonth() + 1 : 12;
  const cur = computeMonthlyTotals(txs, mode, currency, y, endM);
  const cmp = computeMonthlyTotals(txs, mode, currency, y - 1, endM);
  const labels = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  return {
    values: cur,
    compare: cmp,
    labels,
    fullLength: 12,
    periodLabel: '今年',
    compareLabel: '去年',
  };
}

function computeMonthlyTotals(
  txs: TransactionRow[],
  mode: TxKind,
  currency: string,
  year: number,
  endMonth: number,
): number[] {
  const out = Array(endMonth).fill(0) as number[];
  for (const t of txs) {
    if (t.deleted_at) continue;
    if (t.kind !== mode) continue;
    const d = new Date(dayPart(t.date) + 'T00:00:00');
    if (d.getFullYear() !== year) continue;
    const m = d.getMonth();
    if (m >= endMonth) continue;
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', currency);
    out[m] = (out[m] ?? 0) + minor;
  }
  return out;
}

// ─── DateRangeSheet ─────────────────────────────────────────────────────
interface DateRangeSheetProps {
  current: { start: string; end: string };
  onClose: () => void;
  onPick: (range: { start: string; end: string }) => void;
  today: Date;
}

function DateRangeSheet({
  current,
  onClose,
  onPick,
  today,
}: DateRangeSheetProps): JSX.Element {
  const [start, setStart] = useState(current.start);
  const [end, setEnd] = useState(current.end);

  const todayStr = todayIso(today);

  const presets = useMemo(() => {
    const make = (offset: number): string => {
      const d = new Date(today);
      d.setDate(d.getDate() - offset);
      return formatYmd(d);
    };
    const startThisMonth = formatYmd(startOfMonth(today));
    const endThisMonth = todayStr;
    const lastMonth = shiftMonth(today, -1);
    const startLastMonth = formatYmd(startOfMonth(lastMonth));
    const endLastMonth = formatYmd(endOfMonth(lastMonth));
    const startThisYear = formatYmd(startOfYear(today));
    return [
      { label: '最近 7 天', s: make(6), e: todayStr },
      { label: '最近 30 天', s: make(29), e: todayStr },
      { label: '本月', s: startThisMonth, e: endThisMonth },
      { label: '上月', s: startLastMonth, e: endLastMonth },
      { label: '今年', s: startThisYear, e: todayStr },
    ];
  }, [today, todayStr]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="自訂日期區間"
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(36,26,16,0.38)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--bg-app)',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: 'var(--shadow-sheet, 0 -10px 30px rgba(0,0,0,0.18))',
          padding: '8px 16px 28px',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 480,
        }}
      >
        <div style={{ display: 'grid', placeItems: 'center', paddingTop: 4 }}>
          <div
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: 'var(--sand-300)',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0 8px',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 0,
              background: 'transparent',
              color: 'var(--fg-3)',
              font: '500 14px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <div
            style={{
              font: '600 15px var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            自訂日期區間
          </div>
          <div style={{ width: 36 }} />
        </div>

        {/* Presets */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            padding: '6px 0 14px',
          }}
        >
          {presets.map((p) => {
            const sel = start === p.s && end === p.e;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setStart(p.s);
                  setEnd(p.e);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: sel ? 'var(--mocha-700)' : 'var(--bg-surface)',
                  color: sel ? 'var(--cream-50)' : 'var(--fg-2)',
                  border: sel ? 0 : '0.5px solid var(--border-1)',
                  font: '500 12px var(--font-cjk)',
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Start / end */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={fieldBox()}>
            <div style={fieldLabel()}>開始</div>
            <input
              aria-label="起始日期"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              max={end}
              style={fieldInput()}
            />
          </div>
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              font: '500 16px var(--font-num)',
              color: 'var(--fg-3)',
            }}
          >
            –
          </div>
          <div style={fieldBox()}>
            <div style={fieldLabel()}>結束</div>
            <input
              aria-label="結束日期"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              min={start}
              max={todayStr}
              style={fieldInput()}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={() => onPick({ start, end })}
          disabled={!start || !end || start > end}
          style={{
            width: '100%',
            padding: 12,
            background: 'var(--accent)',
            color: '#fff',
            border: 0,
            borderRadius: 14,
            font: '600 15px var(--font-cjk)',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(155,94,64,0.22)',
          }}
        >
          套用
        </button>
      </div>
    </div>
  );
}

function fieldBox(): CSSProperties {
  return {
    flex: 1,
    padding: '14px 14px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-1)',
    borderRadius: 14,
  };
}
function fieldLabel(): CSSProperties {
  return {
    font: '500 10px var(--font-cjk)',
    color: 'var(--fg-3)',
    letterSpacing: '0.1em',
    marginBottom: 4,
  };
}
function fieldInput(): CSSProperties {
  return {
    width: '100%',
    border: 0,
    outline: 0,
    background: 'transparent',
    font: '500 17px var(--font-num)',
    color: 'var(--fg-1)',
    fontVariantNumeric: 'tabular-nums',
  };
}

// Suppress unused `categoriesUnused` warning when CategoryRow type changes.
// (CategoryRow is imported via `useStore`'s typed slice; we re-export
// nothing.)
export type { CategoryRow };
